import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './VideoSection.css';

// Video protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const VideoSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const navigate = useNavigate();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const headerY = useTransform(scrollYProgress, [0.05, 0.3], [80, 0]);
  const headerOpacity = useTransform(scrollYProgress, [0.05, 0.3], [0, 1]);

  const videoScale = useTransform(scrollYProgress, [0.15, 0.5], [0.8, 1]);
  const videoOpacity = useTransform(scrollYProgress, [0.15, 0.5], [0, 1]);

  const glowOpacity = useTransform(scrollYProgress, [0.2, 0.6], [0, 1]);

  const openGallery = () => {
    navigate('/video-gallery');
  };

  useEffect(() => {
    const videoEl = videoRef.current;
    const sectionEl = sectionRef.current;

    if (!videoEl || !sectionEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load video when it comes into view
            if (!isVideoLoaded) {
              videoEl.src = "https://res.cloudinary.com/dhqmcmekn/video/upload/v1764771388/website_video_grh8wb.webm";
              setIsVideoLoaded(true);
            }
            
            // Play video
            const playPromise = videoEl.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.catch(() => {
                // Ignore autoplay failures
              });
            }
          } else {
            videoEl.pause();
          }
        });
      },
      {
        threshold: 0.4,
      }
    );

    observer.observe(sectionEl);

    return () => {
      observer.disconnect();
    };
  }, [isVideoLoaded]);

  return (
    <section className="video-section" ref={sectionRef} id="video">
      <div className="video-section-inner">
        <motion.div
          className="video-header"
          style={{ y: headerY, opacity: headerOpacity }}
        >
          <p className="eyebrow">CINEMATIC STORIES</p>
          <h2 className="section-title video">
            FALL IN LOVE <span className="title-accent">FRAME BY FRAME</span>
          </h2>
          <p className="video-subcopy">
            A curated gallery of wedding films, crafted to feel like timeless cinema.
            Drift into the details, the music, the in‑between moments that make
            your story unforgettable.
          </p>
        </motion.div>

        <div className="video-layout">
          <motion.div
            className="video-shell"
            style={{ scale: videoScale, opacity: videoOpacity }}
          >
            <motion.div
              className="video-glow"
              style={{ opacity: glowOpacity }}
            />
            <div className="video-frame">
              <video
                ref={videoRef}
                className="video-element"
                muted
                loop
                playsInline
                disablePictureInPicture
                controlsList="nodownload"
                onContextMenu={handleContextMenu}
                onDragStart={handleDragStart}
                style={{ pointerEvents: 'none' }}
              />
              {!isVideoLoaded && (
                <div className="video-placeholder">
                  <div className="video-loading-spinner"></div>
                </div>
              )}
              <div className="video-overlay-gradient" />
              <div className="video-overlay-label">FEATURED FILM PREVIEW</div>
            </div>
          </motion.div>

          <motion.div
            className="video-cta-block"
            style={{ opacity: videoOpacity }}
          >
            <p className="video-cta-copy">
              Ready to see more? Our full gallery is here — a dedicated
              space for immersive wedding films and highlight reels.
            </p>
            <button className="video-gallery-button" type="button" onClick={openGallery}>
              <span className="video-gallery-label">OPEN VIDEO GALLERY</span>
              <span className="video-gallery-pill">HOT</span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
